import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { DIR_DELTA, OPPOSITE } from './trainsimConfig.js';

/**
 * Orient a vehicle sprite so its TOP always faces toward the top of
 * the screen — never upside-down. Eastward motion renders normally;
 * westward motion is achieved by mirroring horizontally (scale.x =
 * -1) instead of rotating 180°, which would otherwise flip the
 * artwork onto its roof. Vertical/diagonal motion still rotates,
 * but is constrained to the upper half-plane so the roof stays up.
 */
function setUprightRotation(disp, angle) {
    if (Math.cos(angle) >= 0) {
        disp.rotation = angle;
        if (disp.scale.x < 0) disp.scale.x = -disp.scale.x;
    } else {
        disp.rotation = angle - Math.PI;
        if (disp.scale.x > 0) disp.scale.x = -disp.scale.x;
    }
}

/**
 * Weighted pool of vehicle kinds a spawning car can become. Cars
 * (sedans, 4 colours) appear most often because that's what a real
 * town centre is mostly made of; trucks and buses pepper in as
 * occasional larger silhouettes. The `slot` field maps directly to a
 * SkinResolver key declared in `data/trainsim.config.js`.
 *
 *   lenMul / widMul — width / depth in CELL FRACTIONS. Trucks and
 *                     buses are LONGER than cars so they read as
 *                     distinct vehicle types at any zoom level.
 */
const VEHICLE_KINDS = [
    { slot: 'trainsim.vehicle.car.red',    weight: 4, lenMul: 0.80, widMul: 0.34, fallbackColor: 0xef4444 },
    { slot: 'trainsim.vehicle.car.blue',   weight: 4, lenMul: 0.80, widMul: 0.34, fallbackColor: 0x3b82f6 },
    { slot: 'trainsim.vehicle.car.green',  weight: 4, lenMul: 0.80, widMul: 0.34, fallbackColor: 0x22c55e },
    { slot: 'trainsim.vehicle.car.purple', weight: 4, lenMul: 0.80, widMul: 0.34, fallbackColor: 0xa855f7 },
    { slot: 'trainsim.vehicle.truck',      weight: 2, lenMul: 1.10, widMul: 0.36, fallbackColor: 0xf59e0b },
    { slot: 'trainsim.vehicle.bus',        weight: 2, lenMul: 1.20, widMul: 0.38, fallbackColor: 0xeab308 },
];
const VEHICLE_TOTAL_WEIGHT = VEHICLE_KINDS.reduce((s, v) => s + v.weight, 0);

const pickVehicleKind = () => {
    let r = Math.random() * VEHICLE_TOTAL_WEIGHT;
    for (const v of VEHICLE_KINDS) {
        r -= v.weight;
        if (r <= 0) return v;
    }
    return VEHICLE_KINDS[0];
};

/**
 * Road traffic — a single procedurally-driven car.
 *
 * The car follows the same 8-port routing model the trains use, but
 * restricted to the four cardinal directions (no diagonal road tiles).
 * It drives on the RIGHT side of the centreline relative to its
 * direction of travel (right-hand traffic): so a northbound car uses
 * the EAST lane of a vertical road and a westbound car uses the NORTH
 * lane of a horizontal road. Two opposing flows naturally pass each
 * other on opposite sides of the road without colliding.
 *
 * Speed is dynamic: the car has a personal `baseSpeed`, slows when a
 * car appears in its path, and brakes for red lamps on its next cell
 * (the same lamps that hold trains — so dropping a red lamp stops
 * road traffic the moment a train needs the crossing).
 *
 * Pathfinding is local and stochastic: at each cell boundary the car
 * picks a random open port (other than the one it entered through),
 * which keeps the network alive without requiring any global planner.
 * When the chosen exit leads off the road network the car despawns.
 */

const CARDINAL = [0, 2, 4, 6];
const DIR_ANGLE = {
    0: -Math.PI / 2, // N (heading up)
    2:  0,            // E (heading right)
    4:  Math.PI / 2, // S (heading down)
    6:  Math.PI,      // W (heading left)
};

let _nextCarId = 1;

/**
 * Interpolate angles along the shortest path so a turning car rotates
 * the natural direction (90° not 270°) regardless of which two cardinal
 * angles bracket it.
 */
const lerpAngle = (a0, a1, t) => {
    let diff = a1 - a0;
    while (diff >  Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    return a0 + diff * t;
};

export class TrainSimCar {
    /**
     * @param {object} opts
     * @param {number} opts.col   Spawn cell column.
     * @param {number} opts.row   Spawn cell row.
     * @param {number} opts.dir   Initial travel direction (0/2/4/6).
     * @param {number} opts.cs    Board cell size in pixels.
     * @param {number} opts.speed Base speed (cells per second).
     * @param {number} [opts.color] Body colour (random if omitted).
     * @param {Container} opts.container Pixi container to attach to.
     * @param {object} opts.board Tiny board adapter (see `TrainSimBoard._getCarBoardApi`).
     */
    constructor({ col, row, dir, cs, speed, color, container, board, themeManager }) {
        this.id = `car_${_nextCarId++}`;
        this._col = col;
        this._row = row;
        this._dir = dir;
        this._entryDir = OPPOSITE[dir];
        this._progress = 0;
        this._cs = cs;
        this._baseSpeed = speed;
        this._currentSpeed = 0;
        // Pick the vehicle KIND first so the per-frame `_render`
        // can look up its sprite slot + dimensions without an
        // extra branch every paint. Themed sprite is loaded once
        // and re-tinted only if we had to fall back to procedural
        // graphics (the PNGs already encode their own paint job).
        this._themeManager = themeManager || null;
        this._vehicle = pickVehicleKind();
        this._color = (color != null) ? color : this._vehicle.fallbackColor;
        this._board = board;

        const halfRoad = Math.round(cs * 1.05) / 2;
        // Lane offset = how far the car sits from the centreline. ~45 %
        // of the half-road width puts the car squarely in the middle of
        // one of the two lanes, with enough clearance for an oncoming
        // car in the opposite lane to pass on the same cell.
        this._laneOff = Math.round(halfRoad * 0.45);

        // Lane state — split between "what I'm visually at right now"
        // (`_lane`, a fractional value the renderer reads) and "what I'm
        // committed to" (`_targetLane`, an integer +1 or −1 the board
        // uses for occupancy / overtake checks). They differ briefly
        // while the car is sliding from one lane to the other; once the
        // transition completes they snap back into sync.
        //  +1 = RIGHT of travel direction (normal RHT lane)
        //  −1 = LEFT  of travel direction (overtaking into oncoming side)
        this._lane = 1;
        this._targetLane = 1;

        // Container instead of a bare Graphics so we can stash the
        // theme sprite inside it. The container's `rotation` is what
        // the per-frame movement code mutates — the sprite (or the
        // procedural fallback Graphics) rides along inside.
        this._gfx = new Container();
        this._gfx.zIndex = 12;
        container.addChild(this._gfx);
        this._render();

        this._exitDir = this._computeExitDir();
        if (this._exitDir < 0) {
            this._despawn();
            return;
        }
        this._alive = true;
        this._updateTransform();
    }

    /**
     * Pick a random open port other than the one we entered through.
     * Returns -1 if no valid exit exists (e.g. dead-end with only its
     * entry port open) — the caller will then despawn the car.
     */
    _computeExitDir() {
        const conns = this._board.getRoadConnectionsAt(this._col, this._row);
        if (!conns) return -1;
        const exits = CARDINAL.filter((d) => d !== this._entryDir && conns[d]);
        if (exits.length === 0) return -1;
        return exits[Math.floor(Math.random() * exits.length)];
    }

    update(delta) {
        if (!this._alive) return;
        const dtSec = delta / 60;

        // 1) Decide whether to start/finish an overtake. Only call when
        //    we're settled in a lane (not mid-transition) so we don't
        //    flip-flop mid-slide.
        this._considerLaneChange();

        // 2) Smoothly slide visual `_lane` toward `_targetLane`. Cars
        //    take ~0.3 s to cross from one lane to the other.
        const laneRate = 3.5; // units of lane per second (1 lane = +1→−1 = 2 units → ~0.57 s)
        const laneDiff = this._targetLane - this._lane;
        if (Math.abs(laneDiff) > 0.001) {
            this._lane += Math.sign(laneDiff) * Math.min(Math.abs(laneDiff), laneRate * dtSec);
        } else {
            this._lane = this._targetLane;
        }

        // 3) Speed: brake for car ahead in our PHYSICAL lane (includes
        //    head-on threats during an overtake) and for a CLOSED
        //    level-crossing barrier on the next cell.
        //
        // Real-crossing semantics live in the board API:
        //   `isBarrierClosedForCars(c, r)` returns true when a
        //   lamp's barrier covers (c, r) AND the lamp's signal is
        //   GREEN — meaning the train has been given permission to
        //   cross, so the barrier is down and cars must wait. The
        //   inverse (`lamp.state === 'red'`) means the track is
        //   safe and the barrier is up: cars roll through normally.
        let targetSpeed = this._baseSpeed;
        const ahead = this._board.getCarAhead(this);
        if (ahead) {
            if (ahead.dist < 0.4) targetSpeed = 0;
            else if (ahead.dist < 1.0) targetSpeed = this._baseSpeed * ((ahead.dist - 0.4) / 0.6);
        }

        const nextC = this._col + DIR_DELTA[this._exitDir].dc;
        const nextR = this._row + DIR_DELTA[this._exitDir].dr;
        const barrierClosed = typeof this._board.isBarrierClosedForCars === 'function'
            ? this._board.isBarrierClosedForCars(nextC, nextR)
            : !!this._board.isLampRed?.(nextC, nextR); // legacy fallback
        if (barrierClosed && this._progress > 0.45) {
            targetSpeed = 0;
        }

        const accel = 3.0;
        if (targetSpeed > this._currentSpeed) {
            this._currentSpeed = Math.min(targetSpeed, this._currentSpeed + accel * dtSec);
        } else {
            this._currentSpeed = Math.max(targetSpeed, this._currentSpeed - accel * 2 * dtSec);
        }

        this._progress += this._currentSpeed * dtSec;

        // 4) Cell-boundary handling.
        if (this._progress >= 1) {
            if (!this._board.hasRoadTile(nextC, nextR)) {
                this._despawn();
                return;
            }
            const nextConns = this._board.getRoadConnectionsAt(nextC, nextR);
            const nextEntryDir = OPPOSITE[this._exitDir];
            if (!nextConns || !nextConns[nextEntryDir]) {
                this._despawn();
                return;
            }
            // Last-mile guard: never step into a cell+lane that's
            // already claimed. With overtaking enabled, the lane we
            // claim is our COMMITTED target lane, not our visual one.
            if (this._board.isCellLaneOccupied(nextC, nextR, this._exitDir, this._targetLane, this)) {
                this._progress = 0.999;
                this._currentSpeed = 0;
                this._updateTransform();
                return;
            }
            this._col = nextC;
            this._row = nextR;
            this._dir = this._exitDir;
            this._entryDir = nextEntryDir;
            this._progress -= 1;
            this._exitDir = this._computeExitDir();
            if (this._exitDir < 0) {
                this._despawn();
                return;
            }
            // Approaching a junction or curve while overtaking? Snap
            // back into the right lane before entering — overtaking
            // through a turn is too risky.
            const nextNextConns = this._board.getRoadConnectionsAt(this._col, this._row);
            if (this._targetLane !== 1 && this._isUnsafeForOvertake(nextNextConns)) {
                this._targetLane = 1;
            }
        }

        this._updateTransform();
    }

    /**
     * Overtaking decision tree, evaluated each frame:
     *
     *   IF I'm in my normal lane AND I'm closing on a slower car AND
     *      I'm on a straight stretch AND the opposing lane is clear for
     *      a couple of cells ahead
     *   → swing out into the overtake lane.
     *
     *   IF I'm in the overtake lane AND my right lane is now clear
     *      (the car I was overtaking is behind me / out of the cell)
     *   → tuck back into the right lane.
     */
    _considerLaneChange() {
        // Block re-decisions until the current slide finishes.
        if (Math.abs(this._lane - this._targetLane) > 0.05) return;

        if (this._targetLane === 1) {
            // Need a meaningfully slower car in front to bother.
            const ahead = this._board.getCarAhead(this);
            if (!ahead || ahead.dist > 1.6) return;
            if (ahead.car._baseSpeed >= this._baseSpeed * 0.85) return;
            // Same-direction only — we don't overtake into oncoming traffic
            // we've already braked for.
            if (ahead.car._dir !== this._dir) return;

            // Both the current and next tile must be straight pass-throughs
            // along our axis (no turning/junction overtakes).
            const here = this._board.getRoadConnectionsAt(this._col, this._row);
            if (this._isUnsafeForOvertake(here)) return;

            // The board walks the road for us to make sure the overtake
            // lane is clear for the look-ahead distance.
            if (!this._board.isOvertakeSafe(this, 2)) return;

            this._targetLane = -1;
        } else {
            // Don't return to the right lane until it's clear AND we're
            // no longer overlapping the slower car.
            if (!this._board.isReturnSafe(this)) return;
            this._targetLane = 1;
        }
    }

    /** A tile is unsafe to overtake on if it's a junction, curve, or dead-end. */
    _isUnsafeForOvertake(conns) {
        if (!conns) return true;
        const count = (conns[0] ? 1 : 0) + (conns[2] ? 1 : 0) + (conns[4] ? 1 : 0) + (conns[6] ? 1 : 0);
        if (count !== 2) return true;
        // Must be a straight through-tile aligned with our travel axis.
        return !(conns[this._dir] && conns[OPPOSITE[this._dir]]);
    }

    /**
     * Position the car along the cell-local path between its entry
     * edge and its exit edge.
     *
     *   • Straight pass-throughs, T-junctions, crosses → a straight
     *     chord between the two edge lane-points (existing logic).
     *   • CURVE tiles (exactly two perpendicular cardinals open,
     *     no through-pair) → follow a quarter-arc concentric with
     *     the visible road's arc geometry. That way the car visually
     *     hugs the bend instead of cutting across the inner corner
     *     (which used to leave bumpers visibly clipping outside the
     *     painted asphalt and made queues of cars pile up looking
     *     "stuck" on an S-curve).
     */
    _updateTransform() {
        const cs = this._cs;
        const cx = this._col * cs + cs / 2;
        const cy = this._row * cs + cs / 2;
        const incoming = OPPOSITE[this._entryDir];

        // Try the curve-aware path first. Falls through to the straight
        // chord whenever the current cell isn't a pure 90° corner.
        const conns = this._board.getRoadConnectionsAt?.(this._col, this._row);
        if (conns && TrainSimCar._isCurveCell(conns)) {
            this._updateTransformCurve(cx, cy, conns);
            return;
        }

        const a = this._edgePoint(cx, cy, this._entryDir, incoming);
        const b = this._edgePoint(cx, cy, this._exitDir, this._exitDir);

        const t = this._progress;
        this._gfx.x = a.x + (b.x - a.x) * t;
        this._gfx.y = a.y + (b.y - a.y) * t;

        // Smoothly rotate from incoming heading to outgoing heading
        // across the cell. For straight-throughs they match, so the
        // rotation is constant; for turns the body sweeps naturally.
        // Routed through `setUprightRotation` so the car NEVER lands
        // wheels-up on a westbound segment — instead it horizontally
        // mirrors the sprite, keeping the roof toward the sky.
        setUprightRotation(
            this._gfx,
            lerpAngle(DIR_ANGLE[incoming], DIR_ANGLE[this._exitDir], t),
        );
    }

    /**
     * Detects "this cell is rendered as a road curve" by matching
     * the same condition `drawTrainsimRoad` uses to render a quarter-
     * annulus: exactly two perpendicular cardinal ports open. We
     * keep this in step with the painter so cars always follow the
     * same geometry the road is drawn with.
     */
    static _isCurveCell(conns) {
        const count = (conns[0] ? 1 : 0) + (conns[2] ? 1 : 0) + (conns[4] ? 1 : 0) + (conns[6] ? 1 : 0);
        if (count !== 2) return false;
        if (conns[0] && conns[4]) return false; // N-S through
        if (conns[2] && conns[6]) return false; // E-W through
        return true;
    }

    /**
     * Curve-aware positioning: drive along the road's quarter-arc.
     *
     * The pivot is the cell corner the road bends AROUND (same one the
     * road painter uses), and the centreline arc sits at radius cs/2
     * from that pivot — so at the cell edges the position lines up
     * pixel-for-pixel with a straight tile's centreline. Lane is mapped
     * to a radius shift:
     *
     *   – LEFT turn (sign(diff) < 0): lane=+1 (RHT) → OUTER → radius+laneOff
     *   – RIGHT turn (sign(diff) > 0): lane=+1 (RHT) → INNER → radius−laneOff
     *
     * which collapses to: radius = c − sign(diff) · laneOff · lane. The
     * fractional `_lane` (used during overtake slides) feeds in
     * directly, so a car mid-overtake smoothly drifts between the
     * inner and outer concentric arcs.
     */
    _updateTransformCurve(cx, cy, conns) {
        const cs = this._cs;
        // Pivot in world coords — the cell CORNER the road wraps
        // around. Matches the four cases in `drawTrainsimRoad`.
        let px = 0, py = 0;
        if (conns[0] && conns[2])       { px = 1; py = 0; } // N+E → top-right
        else if (conns[2] && conns[4])  { px = 1; py = 1; } // E+S → bottom-right
        else if (conns[4] && conns[6])  { px = 0; py = 1; } // S+W → bottom-left
        else if (conns[6] && conns[0])  { px = 0; py = 0; } // W+N → top-left
        const pivotX = (this._col + px) * cs;
        const pivotY = (this._row + py) * cs;

        // Angle from the pivot to each port's edge midpoint.
        const portAngle = (dir) => {
            let ex = cx, ey = cy;
            if (dir === 0) ey = cy - cs / 2;
            if (dir === 2) ex = cx + cs / 2;
            if (dir === 4) ey = cy + cs / 2;
            if (dir === 6) ex = cx - cs / 2;
            return Math.atan2(ey - pivotY, ex - pivotX);
        };
        const startAng = portAngle(this._entryDir);
        const endAng   = portAngle(this._exitDir);

        // Always take the shorter 90° arc. `_isCurveCell` guarantees
        // start/end ports are perpendicular, but the wrap defends
        // against the −π/+π boundary case anyway.
        let diff = endAng - startAng;
        while (diff >  Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        const sign = diff >= 0 ? 1 : -1;

        const halfCell = cs / 2;
        // Lane=+1 (RHT) maps to OUTER on a left turn, INNER on a
        // right turn — see the doc comment above.
        const radius = halfCell - sign * this._laneOff * this._lane;

        const t = this._progress;
        const ang = startAng + diff * t;
        this._gfx.x = pivotX + Math.cos(ang) * radius;
        this._gfx.y = pivotY + Math.sin(ang) * radius;

        // Heading = tangent along the arc, oriented in the direction
        // of motion. Validated against DIR_ANGLE at the endpoints so
        // the car enters/leaves the curve with the same rotation a
        // chord-only neighbour cell would draw it. Funnelled through
        // `setUprightRotation` so the tangent never tips the car
        // onto its roof — westbound arcs get a horizontal mirror
        // applied instead of a full 180° spin.
        setUprightRotation(
            this._gfx,
            Math.atan2(
                Math.cos(ang) * sign,
               -Math.sin(ang) * sign,
            ),
        );
    }

    /**
     * Resolve a (cell-edge, travel-direction) pair to a world-space
     * point. The edge fixes one coordinate (which side of the cell);
     * the travel direction picks which lane the car is in. We multiply
     * the per-direction "right offset" by the car's fractional `_lane`
     * (+1 → fully right, −1 → fully left, anything in between → mid-
     * slide during a lane change) so the renderer smoothly slides the
     * car between lanes during an overtake.
     */
    _edgePoint(cx, cy, edgeDir, travelDir) {
        const half = this._cs / 2;
        const lane = this._laneOff * this._lane;
        let x = cx, y = cy;
        if (edgeDir === 0) y = cy - half;
        if (edgeDir === 2) x = cx + half;
        if (edgeDir === 4) y = cy + half;
        if (edgeDir === 6) x = cx - half;
        if (travelDir === 0) x += lane;
        if (travelDir === 2) y += lane;
        if (travelDir === 4) x -= lane;
        if (travelDir === 6) y -= lane;
        return { x, y };
    }

    _render() {
        const g = this._gfx;
        // Tear down any previous children before painting fresh —
        // `_render` is called once at construction time, but doing
        // a clean wipe keeps the method idempotent for future
        // mid-life repaints (e.g. theme swap, debug overrides).
        g.removeChildren();

        const v = this._vehicle;
        const len = Math.max(10, Math.round(this._cs * v.lenMul));
        const wid = Math.max(6,  Math.round(this._cs * v.widMul));

        // ── Theme sprite (preferred path) ──────────────────────────
        // The PNG already encodes the vehicle's paint, headlights,
        // windows, etc. so we don't tint it — just stamp it in at
        // the body size. Sprite is centred on the container's
        // origin so the existing rotation/position logic keeps
        // working unchanged.
        const sprite = this._themeManager?.createSprite?.(
            v.slot, len, wid, { fitMode: 'contain' },
        );
        if (sprite) {
            sprite.x = 0;
            sprite.y = 0;
            // Vehicle PNGs in `assets/simulator/` follow the same
            // EAST-facing convention as the tram sprites (their
            // "front" points along +x at rotation 0), so the car's
            // existing DIR_ANGLE rotation logic lines up without
            // any per-sprite pivot. Flip to `Math.PI / 2` if the
            // artist later switches to UP-facing assets.
            sprite.rotation = 0;
            g.addChild(sprite);
            return;
        }

        // ── Procedural fallback ────────────────────────────────────
        // Identical to the original Graphics path so cars still
        // render legibly if the asset is missing or the SkinResolver
        // hasn't finished loading.
        const body = new Graphics();
        const r = Math.max(1, Math.round(this._cs * 0.04));
        body.roundRect(-len / 2, -wid / 2, len, wid, r)
            .fill({ color: this._color });
        body.rect(-len / 2 + 1, -wid / 2 + 1, len - 2, 1)
            .fill({ color: 0x000000, alpha: 0.18 });
        const wsX = len / 2 - len * 0.38;
        const wsW = len * 0.22;
        body.rect(wsX, -wid / 2 + 1, wsW, wid - 2)
            .fill({ color: 0x12162b, alpha: 0.75 });
        const hlR = Math.max(0.8, this._cs * 0.018);
        body.circle(len / 2 - 1, -wid / 2 + Math.max(2, wid * 0.18), hlR)
            .fill({ color: 0xfff6c2 });
        body.circle(len / 2 - 1,  wid / 2 - Math.max(2, wid * 0.18), hlR)
            .fill({ color: 0xfff6c2 });
        g.addChild(body);
    }

    _despawn() {
        this._alive = false;
        if (this._gfx) {
            this._gfx.destroy();
            this._gfx = null;
        }
    }

    destroy() {
        this._despawn();
    }
}
